import IconTrash1 from "@/icons/IconTrash1";
import React, { useCallback, useState } from "react";
import Select, { SingleValue } from "react-select";
import Option from "react-select/dist/declarations/src/components/Option";
import { NetSuiteList, NetSuiteMap } from "./NetSuiteMappingTable";
import { debounce } from "@/utils/fn";

interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface Props {
  mappedField: NetSuiteMap;
  i: number;
  updateMappedField: (i: number, field: NetSuiteMap) => void;
  metadata: Option[];
  isLoading: boolean;
  handleModal: (field: NetSuiteMap) => void;
  fetchList: (fields: NetSuiteMap[]) => void;
  listFields: NetSuiteList[];
  deleteRow: (i: number) => void;
  saveRecord: () => void;
  showSavedMessage: () => void;
  metadataType: string;
  requiredFields: string[];
  isHydrating: boolean;
}

export default function NetSuitePanelRow(props: Props) {
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const debouncedSaveRecord = useCallback(debounce(props.saveRecord, 1000), []);

  const handleChange = (callback: () => void) => {
    callback();
    props.showSavedMessage();
    };

  return (
    <tr key={props.i}>
      <td className="pb-4 pr-5 pl-4">
        <div className="relative text-slate-800 text-sm font-normal border border-slate-300 rounded-lg">
          <select
            className="w-full text-sm font-normal bg-white rounded-lg border-none px-4 py-2 appearance-none focus:outline-none focus:ring-0"
            value={props.mappedField.netSuiteField?.value || ""}
            onChange={(e) =>
              handleChange(() => {
                const updatedField = {
                  ...props.mappedField,
                  netSuiteField: {
                    value: e.target.value,
                    label: e.target.options[e.target.selectedIndex].text
                  }
                };

                if (
                  e.target.value?.toLowerCase() === "isperson" ||
                  e.target.value?.toLowerCase() === "subsidiary"
                ) {
                  updatedField.control = "List";
                  props.fetchList([updatedField]);
                }

                props.updateMappedField(props.i, updatedField);
              })
            }
            disabled={props.isLoading || props.isHydrating}
          >
            {props.isLoading ? (
              <option>Loading...</option>
            ) : (
              props.metadata.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            )}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <i className="transition fa-solid fa-angle-down  text-slate-400 mr-1"></i>
          </div>
        </div>
      </td>
      <td className="pb-4 pr-5 ">
        <div className={`relative text-slate-800 text-sm font-normal border border-slate-300 rounded-lg ${(props.metadataType === "Transaction" || props.metadataType === "Products"|| props.metadataType === "Contact") ? "opacity-50" : ""}`}>
          <select
            className={`w-full text-sm font-normal rounded-lg border-none px-4 py-2 appearance-none focus:outline-none focus:ring-0 ${(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") ? "bg-slate-100 cursor-not-allowed" : "bg-white cursor-pointer"}`}
            value={props.mappedField.control || ""}
            onChange={(e) =>
              handleChange(() => {
                const newControl = e.currentTarget.value;
                props.updateMappedField(props.i, {
                  ...props.mappedField,
                  control: e.currentTarget.value
                });
                if (newControl === "List") {
                  props.fetchList([props.mappedField]);
                }
              })
            }
            disabled={(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") || props.isHydrating}
          >
            <option></option>
            <option>Textbox</option>
            <option>Text Area</option>
            <option>Checkbox</option>
            <option>List</option>
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <i className="transition fa-solid fa-angle-down  text-slate-400 mr-1"></i>
          </div>
        </div>
      </td>
      <td className="pb-4  ">
        {props.mappedField?.control === "List" ? (
          <Select
            className={`w-full text-sm font-normal rounded-lg border-none py-2 appearance-none focus:outline-none focus:ring-0 ${(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") ? "bg-slate-100" : "bg-white cursor-pointer"}`}
            options={props.listFields
              .filter(
                (item) =>
                  item.netSuiteField === props.mappedField.netSuiteField?.value
              )
              .flatMap((listField, index) => {
                return listField.listItems;
              })}
            value={props.listFields
              .filter(
                (item) =>
                  item.netSuiteField === props.mappedField.netSuiteField?.value
              )
              .map((options) =>
                options.listItems.find(
                  (option) => option.value === props.mappedField.defaultValue
                )
              )}
            onChange={(e) =>
              handleChange(() =>
                props.updateMappedField(props.i, {
                  ...props.mappedField,
                  defaultValue: e?.value
                })
              )
            }
            isLoading={props.isLoading}
            isDisabled={(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") || props.isHydrating}
          />
        ) : (
          <input
            className={`w-full h-max focus:outline-none p-2 text-slate-800 text-sm font-normal border border-slate-300 rounded-lg ${(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") ? "bg-slate-100 cursor-not-allowed" : "cursor-pointer"}`}
            value={props.mappedField.defaultValue || ""}
            placeholder="Enter default value"
            onChange={(e) =>
              handleChange(() =>
                props.updateMappedField(props.i, {
                  ...props.mappedField,
                  defaultValue: e.currentTarget.value
                })
              )
            }
            disabled={(props.metadataType === "Transaction" || props.metadataType === "Products" || props.metadataType === "Contact") || props.isHydrating}
            ></input>
        )}
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className={`input w-full ${
            props.metadataType === "Transaction" ||
            props.metadataType === "Products" ||
            props.metadataType === "Contact"
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          checked={props.mappedField.create}
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(props.i, {
                ...props.mappedField,
                create: !props.mappedField.create
              })
            )
          }
          disabled={
            props.metadataType === "Transaction" ||
            props.metadataType === "Products" ||
            props.metadataType === "Contact" ||
            props.isHydrating ||
            (props.requiredFields.includes(
              props.mappedField.netSuiteField?.value || ""
            ))
          }
        ></input>
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className={`input w-full ${
            props.mappedField.netSuiteField?.value?.includes(':')
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          checked={props.mappedField.usedForSearching}
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(props.i, {
                ...props.mappedField,
                usedForSearching: !props.mappedField.usedForSearching
              })
            )
          }
          disabled={props.isHydrating || props.mappedField.netSuiteField?.value?.includes(':')}
        ></input>
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={props.mappedField.showWhenSelected}
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(props.i, {
                ...props.mappedField,
                showWhenSelected: !props.mappedField.showWhenSelected
              })
            )
          }
          disabled={props.isHydrating}
        ></input>
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={props.mappedField.showInSearch}
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(props.i, {
                ...props.mappedField,
                showInSearch: !props.mappedField.showInSearch
              })
            )
          }
          disabled={props.isHydrating}
        ></input>
      </td>

      <td className="text-center justify-center items-center place-self-center">
        {
          <div
            className={`py-2 px-4 justify-center flex ${props.isHydrating ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            onClick={() => !props.isHydrating && props.deleteRow(props.i)}
          >
            <IconTrash1 />
          </div>
        }
      </td>
    </tr>
  );
}
